import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Loader2 } from 'lucide-react';
import PageHero from '../components/PageHero';
import CtaBanner from '../components/CtaBanner';
import { api } from '../lib/api';
import { NEWS } from '../data/mock';

function formatDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return iso; }
}

function slugify(s) {
  return (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

// Shape the API response + fallback mock entries into a uniform card model
function normalise(apiPosts) {
  const fromApi = (apiPosts || []).map((p) => ({
    slug: p.slug,
    title: p.title,
    date: formatDate(p.published_at),
    img: p.cover_image || '/assets/why-is-it-important-to-set-up-ivr-payment-for-your-business.jpg',
    excerpt: p.excerpt || '',
  }));
  if (fromApi.length > 0) return fromApi;
  // Fallback to the original static mock data if the DB is empty.
  return NEWS.map((n) => ({ ...n, slug: slugify(n.title) }));
}

export default function Blog() {
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    api.get('/api/public/blog')
      .then((r) => setPosts(normalise(r.data)))
      .catch(() => setPosts(normalise([])));
  }, []);

  if (posts === null) {
    return (
      <>
        <PageHero eyebrow="Insights" title="Blog & Resources" subtitle="Guides, news and best practices to get the most from your business communications." breadcrumb="Blog" />
        <div className="py-20 text-center text-slate-500"><Loader2 size={18} className="animate-spin inline mr-2" /> Loading articles…</div>
      </>
    );
  }

  const featured = posts[0];
  const rest = posts.slice(1);

  return (
    <>
      <PageHero
        eyebrow="Insights"
        title="Blog & Resources"
        subtitle="Guides, news and best practices to get the most from your business communications."
        breadcrumb="Blog"
      />

      <section className="bg-white">
        <div className="container-x section-pad">
          {featured && (
            <article className="grid lg:grid-cols-2 gap-10 items-center mb-16">
              <Link to={`/blog/${featured.slug}`} className="relative block group" data-testid="featured-article-link">
                <div className="absolute -inset-5 rounded-3xl bg-gradient-to-br from-violet-100 to-fuchsia-50 -z-10" />
                <img src={featured.img} alt={featured.title} className="rounded-2xl shadow-xl w-full group-hover:scale-[1.02] transition-transform" />
              </Link>
              <div>
                <span className="eyebrow">Featured story</span>
                <h2 className="text-3xl md:text-4xl font-extrabold mt-4 leading-tight">
                  <Link to={`/blog/${featured.slug}`} className="hover:text-violet-600 transition-colors">{featured.title}</Link>
                </h2>
                <p className="flex items-center gap-2 text-sm text-slate-500 mt-3">
                  <Calendar size={14} /> {featured.date}
                </p>
                <p className="text-slate-600 mt-4 leading-relaxed">{featured.excerpt}</p>
                <Link to={`/blog/${featured.slug}`} className="btn-primary mt-6" data-testid="featured-read-article-btn">
                  Read article <ArrowRight size={16} />
                </Link>
              </div>
            </article>
          )}

          {rest.length > 0 && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {rest.map((n) => (
                <Link
                  key={n.slug}
                  to={`/blog/${n.slug}`}
                  className="card-modern p-0 overflow-hidden group block"
                  data-testid={`blog-card-${n.slug}`}
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={n.img} alt={n.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                  </div>
                  <div className="p-6">
                    <p className="text-xs uppercase tracking-wider text-violet-600 font-semibold mb-2">{n.date}</p>
                    <h3 className="font-bold text-slate-900 mb-2 leading-snug group-hover:text-violet-700 transition-colors">{n.title}</h3>
                    <p className="text-sm text-slate-600 mb-4">{n.excerpt}</p>
                    <span className="text-violet-600 font-semibold text-sm flex items-center gap-1">Read more <ArrowRight size={14} /></span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="bg-gradient-to-br from-violet-50 to-fuchsia-50 border-y border-violet-100">
        <div className="container-x py-16 md:py-20 text-center">
          <span className="eyebrow">Knowledge Base</span>
          <h2 className="text-3xl md:text-4xl font-extrabold mt-4">Looking for step-by-step help?</h2>
          <p className="text-slate-600 max-w-2xl mx-auto mt-3">
            Browse our user guides, video tutorials and image assets in the Resources hub.
          </p>
          <Link to="/resources" className="btn-primary mt-6 inline-flex" data-testid="blog-to-resources">
            Visit Resources <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      <CtaBanner title="Stay updated" subtitle="Get our latest articles delivered straight to your inbox." primary="Subscribe" />
    </>
  );
}
